
import { filter } from 'rxjs/operators';
import { Params, Router, NavigationEnd } from '@angular/router';
import { METRICS, METRIC_ACTIONGROUPS, METRIC_ALERTS, METRIC_DEFINITIONS } from 'app/services/globals';
import { ResourceId } from 'app/shared/utils/resource-id';
import { BehaviorSubject, Observable } from 'rxjs';
import { routingConstants } from 'app/shared/constants/routing.const';
import { Injector } from '@angular/core';
import { AuthConstants } from './authentication/auth-constants';
import { ValidationConstants } from 'shell-models-lib';

export abstract class UrlInformationBaseService {
    public abstract resourceProviderName: string;
    public abstract resourceType: string;
    public subscriptionId: string;
    public resourceGroupName: string;
    public resourceName: string;
    public resourceId: string;
    public resourceTags: { [propertyName: string]: string };
    public isResourceProvided = false;
    public subId$: BehaviorSubject<string | undefined> = new BehaviorSubject(undefined);
    public tenantId: string | undefined;
    public resourceTypeNamesInSearch: string[] = [];

    protected readonly authoringRoute = '/' + routingConstants.authoringPath;
    protected readonly overviewRoute = '/' + routingConstants.overviewPath;
    protected readonly monitoringRoute = '/' + routingConstants.monitoringPath;
    protected readonly managementRoute = '/' + routingConstants.managementPath;
    protected readonly cwRoute = '/' + routingConstants.authoringPath + '/' + routingConstants.copywizard;
    protected readonly azureBase: string;
    protected _resourceId: ResourceId;

    private _isOverviewActive = false;
    private _isAuthoringActive = false;
    private _isMonitoringActive = false;
    private _isManagementActive = false;
    private _resourceLocation$: BehaviorSubject<string | undefined> = new BehaviorSubject(undefined);
    private _isCopyWizardActive: boolean = false;

    public get isOverViewActive(): boolean {
        return this._isOverviewActive;
    }

    public get isAuthoringActive(): boolean {
        return this._isAuthoringActive;
    }

    public get isMonitoringActive(): boolean {
        return this._isMonitoringActive;
    }

    public get isManagementActive(): boolean {
        return this._isManagementActive;
    }

    public get isCopyWizardActive(): boolean {
        return this._isCopyWizardActive;
    }

    public get resourceLocation(): string {
        return this._resourceLocation$.value;
    }

    protected get resourceLocation$(): Observable<string> {
        return this._resourceLocation$;
    }

    constructor(injector: Injector) {
        const refreshActiveState = (router: Router) => {
            this._isOverviewActive = router.isActive(this.overviewRoute, false);
            this._isAuthoringActive = router.isActive(this.authoringRoute, false);
            this._isMonitoringActive = router.isActive(this.monitoringRoute, false);
            this._isManagementActive = router.isActive(this.managementRoute, false);
            this._isCopyWizardActive = router.isActive(this.cwRoute, false);
        };

        setTimeout(() => {
            const router = injector.get<Router>(Router);

            if (router && router.events) {
                router.events.pipe(
                    filter((event) => event instanceof NavigationEnd))
                    .subscribe((): void => {
                        refreshActiveState(router);
                    });

                refreshActiveState(router);
            }
        }, 0);
    }

    public getAzureBase(): string {
        return `subscriptions/${this.subscriptionId}/resourcegroups/${this.resourceGroupName}`;
    }

    public getResourceId(): string {
        return `${this.getAzureBase()}/providers/${this.resourceProviderName}/${this.resourceType}/${this.resourceName}`;
    }

    public getBaseResource(): string {
        return `subscriptions/${this.subscriptionId}/providers/${this.resourceProviderName}`;
    }

    public getSubId(): string {
        return `subscriptions/${this.subscriptionId}`;
    }

    public getResourceInAzurePortalLink(): string {
        return `${AuthConstants.AZURE_PORTAL}/#resource/${this.getResourceId()}`;
    }

    public setResourceTags(tags: { [propertyName: string]: string }): void {
        this.resourceTags = tags;
    }

    public getMetricAlertsEndpoint(): string {
        return `${this.getAzureBase()}/providers/${METRIC_ALERTS}`;
    }

    public getMetricDefinitionsEndpoint(): string {
        return `${this.getResourceId()}/providers/${METRIC_DEFINITIONS}`;
    }

    public getMetricHistoryEndpoint(): string {
        return `${this.getResourceId()}/providers/${METRICS}`;
    }

    public getActionGroupEndpoint(): string {
        return `${this.getAzureBase()}/providers/${METRIC_ACTIONGROUPS}`;
    }

    protected setResourceLocation(location: string): void {
        this._resourceLocation$.next(location);
    }

    protected setResourceIdInfo(): void {
        const resourceId = ResourceId.parseFrom(this.resourceId, this.resourceProviderName, this.resourceType);

        if (resourceId) {
            this._resourceId = resourceId;
            this.subscriptionId = this._resourceId.subscriptionId;
            this.resourceGroupName = this._resourceId.resourceGroupName;
            // This will make sure we don't call any API for invalid workspace name
            this.resourceName =
                ValidationConstants.alphanumericDash.test(this._resourceId.topResourceName)
                    ? this._resourceId.topResourceName
                    : '';

            this.isResourceProvided = true;
            this.subId$.next(this.subscriptionId);
        }
    }

    protected setResourceInfo(queryParams: Params): void {
        const resIdOptions = this.resourceTypeNamesInSearch.map((item) => queryParams[item]).filter((item) => !!item);
        if (resIdOptions[0]) {
            this.resourceId = decodeURIComponent(resIdOptions[0]);
            this.setResourceIdInfo();
        }
    }
}
